{
  "$schema": "https://json-schema.org/draft/2020-12/schema",
  "$id": "https://raw.githubusercontent.com/jwildfire/safety.viz/main/src/data/schema/hep-explorer.json",
  "title": "safety.viz hep-explorer data contract",
  "description": "Long-format liver-lab data: one record per participant per measure per visit/day (HEP-DATA-001). Column names are supplied by the settings mapping; the hep-explorer standardizes each value to ×ULN and ×Baseline, reduces to one point per participant (peak X measure vs peak Y measure), and removes missing/non-numeric results with a reported count (HEP-DATA-003). The four liver measures (ALT/AST/TB/ALP) are matched from the measure column via measure_values.",
  "type": "object",
  "required": ["data", "settings"],
  "properties": {
    "data": {
      "type": "array",
      "minItems": 1,
      "items": { "type": "object" },
      "description": "d3.csv()-style records; every row carries the measure, result, participant, and ULN columns named in settings, one row per participant per measure per visit/day."
    },
    "settings": {
      "type": "object",
      "description": "Column mappings and rendering options; merged onto the module's DEFAULT_SETTINGS, so only overrides need to be supplied (HEP-DATA-003).",
      "required": ["id_col", "measure_col", "value_col", "normal_col_high"],
      "properties": {
        "id_col": {
          "type": "string",
          "default": "USUBJID",
          "description": "Participant identifier column; one plotted point per participant (HEP-DATA-001)."
        },
        "measure_col": {
          "type": "string",
          "default": "TEST",
          "description": "Column holding the measure name; required in data. Matched to the ALT/AST/TB/ALP keys via measure_values (HEP-DATA-002)."
        },
        "value_col": {
          "type": "string",
          "default": "STRESN",
          "description": "Column holding the numeric result; required in data. Non-numeric results are removed with a logged count (HEP-DATA-003)."
        },
        "unit_col": {
          "type": "string",
          "default": "STRESU",
          "description": "Optional unit column, appended to measure labels and shown in the linked listing."
        },
        "normal_col_high": {
          "type": "string",
          "default": "STNRHI",
          "description": "Upper limit of normal (ULN); required in data — the ×ULN standardization divides each value by it (HEP-DISPLAY-002)."
        },
        "normal_col_low": {
          "type": ["string", "null"],
          "default": "STNRLO",
          "description": "Optional lower limit of normal, carried into the linked listing."
        },
        "studyday_col": {
          "type": ["string", "null"],
          "default": "DY",
          "description": "Optional study-day column; drives the timing test and visit-path ordering. When absent, a per-participant per-measure input-order sequence is derived (HEP-DATA-004, HEP-SELECT-004)."
        },
        "visit_col": {
          "type": ["string", "null"],
          "default": "VISIT",
          "description": "Optional categorical visit column; labels the visit-path overlay and pairs the X/Y trajectory points (HEP-SELECT-003)."
        },
        "visitn_col": {
          "type": ["string", "null"],
          "default": "VISITNUM",
          "description": "Optional numeric visit column; orders visit-keyed series when present."
        },
        "arm_col": {
          "type": ["string", "null"],
          "default": "ARM",
          "description": "Treatment-arm column, structural for the migration view — it decides which side of the centre column a participant's flow leaves from. Auto-detected across ARM, ACTARM, TRT01A, TREATMENT and TRTA when the named column is absent, and deliberately NOT in this contract's required list, so arm-less data still renders the scatter and composite views (HEP-ARM-001)."
        },
        "placebo_arm": {
          "type": ["string", "null"],
          "default": null,
          "description": "Arm value plotted on the left (placebo) side of the migration Sankey; when null it is auto-detected by matching the arm values against /placebo|control/i (HEP-ARM-002)."
        },
        "active_arms": {
          "type": ["array", "string", "null"],
          "items": { "type": "string" },
          "default": null,
          "description": "Arm values plotted on the right (active) side; when null every non-placebo arm pools right and the pooled arms are named in the notes (HEP-ARM-003)."
        },
        "baseline_col": {
          "type": ["string", "null"],
          "default": null,
          "description": "Optional baseline-flag column (e.g. ABLFL). When supplied, the flagged record is the baseline, outranking the day-0-else-earliest heuristic (HEP-CORE-003)."
        },
        "baseline_value": {
          "type": "string",
          "default": "Y",
          "description": "The value of baseline_col that marks the baseline record (HEP-CORE-003)."
        },
        "jaundice_uln": {
          "type": "number",
          "default": 2,
          "description": "New-onset-jaundice threshold on the total-bilirubin ×ULN scale: flagged when baseline is at or below it and the on-treatment maximum exceeds it. Defaults to the composite plot's bilirubin cutpoint so the flag and the quadrants stay mutually consistent (HEP-CORE-006)."
        },
        "hide_unchanged": {
          "type": "boolean",
          "default": false,
          "description": "Migration view: suppress the diagonal (no-migration) ribbons; the hidden participant count stays in the notes and the cross tables (HEP-MIG-013)."
        },
        "measure_values": {
          "type": "object",
          "default": {
            "ALT": "Aminotransferase, alanine (ALT)",
            "AST": "Aminotransferase, aspartate (AST)",
            "TB": "Total Bilirubin",
            "ALP": "Alkaline phosphatase (ALP)"
          },
          "description": "Map of the short measure key (ALT/AST/TB/ALP) to the full measure string in the data (HEP-DATA-002)."
        },
        "view": {
          "type": "string",
          "enum": ["scatter", "migration", "composite"],
          "default": "scatter",
          "description": "Initial view mode: `scatter` (eDISH/mDISH one-point-per-participant scatter), `migration` (the bidirectional baseline → peak on-treatment Sankey mirrored about the baseline categorization, with one cross table per treatment arm — Amirzadegan et al., Drug Safety 2025, Fig 3; needs arm_col mapped), or `composite` (baseline-referenced composite plot for subjects with abnormal baseline liver tests — pretreatment and on-treatment eDISH panels, a four-panel ×Baseline shift plot, and a migration table) (HEP-COMP-006, HEP-MIG-001)."
        },
        "x_default": {
          "type": "string",
          "default": "ALT",
          "description": "Measure plotted on the x-axis on first render (HEP-CTRL-001)."
        },
        "y_default": {
          "type": "string",
          "default": "TB",
          "description": "Measure plotted on the y-axis on first render (HEP-CTRL-002)."
        },
        "x_options": {
          "type": "array",
          "items": { "type": "string" },
          "default": ["ALT", "AST", "TB", "ALP"],
          "description": "Measures offered by the X-axis Measure control (HEP-CTRL-001)."
        },
        "y_options": {
          "type": "array",
          "items": { "type": "string" },
          "default": ["TB"],
          "description": "Measures offered by the Y-axis Measure control; a single option drops the control (HEP-CTRL-002)."
        },
        "cuts": {
          "type": "object",
          "default": {
            "TB": { "relative_uln": 2, "relative_baseline": 4.8 },
            "ALP": { "relative_uln": 1, "relative_baseline": 3.8 },
            "rRatio": { "relative_uln": 5, "relative_baseline": 5 },
            "defaults": { "relative_uln": 3, "relative_baseline": 3.8 }
          },
          "description": "Per-measure Hy's-Law cutpoints keyed by measure then display mode; a `defaults` entry back-fills any measure without its own cuts (HEP-QUAD-001)."
        },
        "calculate_palt": {
          "type": "boolean",
          "default": false,
          "description": "Opt in to computing the P_ALT hepatocyte-loss estimate (ALT AUC x peak ALT^0.18 / 10^5; Chung et al., PMID 30303523) from each participant's ALT trajectory when no pre-computed value is mapped. Off by default: the estimate assumes ALT in IU/L and a study-day axis dense enough for a trapezoidal AUC (HEP-PALT-001, HEP-PALT-002)."
        },
        "visit_window": {
          "type": "number",
          "default": 30,
          "description": "Timing window (days): points whose peak-X and peak-Y days are within this many days render filled, else hollow (HEP-CTRL-008, HEP-DISPLAY-005)."
        },
        "r_ratio_filter": {
          "type": "boolean",
          "default": true,
          "description": "Whether to render the R-Ratio range filter control (HEP-CTRL-010)."
        },
        "r_ratio": {
          "type": "array",
          "items": { "type": ["number", "null"] },
          "default": [0, null],
          "description": "Initial R-Ratio [min, max]; a null max is resolved from the data on first render (HEP-CTRL-010)."
        },
        "unscheduled_visits": {
          "type": "boolean",
          "default": true,
          "description": "Include records taken at an unscheduled visit (matched by unscheduled_visit_values, else by unscheduled_visit_pattern, against visit_col) in the baseline and peak reduction. Included by default, unlike results-over-time: turning it off re-derives every baseline and peak from the scheduled records alone, and the note above the chart states how many records were excluded (HEP-CTRL-018)."
        },
        "unscheduled_visit_pattern": {
          "type": "string",
          "default": "/unscheduled|early termination/i",
          "description": "Regular expression, in /source/flags string form, identifying an unscheduled visit (HEP-DATA-013)."
        },
        "unscheduled_visit_values": {
          "type": ["array", "null"],
          "items": { "type": "string" },
          "default": null,
          "description": "Explicit list of unscheduled visit names; takes precedence over unscheduled_visit_pattern when set (HEP-DATA-013)."
        },
        "filters": {
          "$ref": "#/$defs/filterList",
          "description": "Optional filter columns rendered as controls (HEP-CTRL-011). Each entry is a column name or a { value_col, label, start, all, multiple } spec: `start` is the value the filter opens on, `all` (default true) is whether an \"All\" option is offered, and `multiple` (default false) makes it a checkbox multiselect."
        },
        "groups": {
          "$ref": "#/$defs/fieldList",
          "description": "Optional color-by columns for grouping the points (HEP-CTRL-009)."
        },
        "details": {
          "$ref": "#/$defs/fieldList",
          "description": "Optional listing columns; defaults derive from the measure/day/value mappings (HEP-SELECT-006)."
        }
      }
    }
  },
  "$defs": {
    "fieldList": {
      "type": "array",
      "items": {
        "anyOf": [
          { "type": "string" },
          {
            "type": "object",
            "required": ["value_col"],
            "properties": {
              "value_col": { "type": "string" },
              "label": { "type": "string" }
            }
          }
        ]
      }
    },
    "filterList": {
      "type": "array",
      "items": {
        "anyOf": [
          { "type": "string" },
          {
            "type": "object",
            "required": ["value_col"],
            "properties": {
              "value_col": { "type": "string" },
              "label": { "type": "string" },
              "start": {
                "type": ["string", "number", "boolean", "array", "null"],
                "default": null,
                "description": "The value the filter opens on; an array of values for a `multiple` filter. 0 and false are real values, not an absent start. A value the data lacks is dropped with a console warning."
              },
              "all": {
                "type": "boolean",
                "default": true,
                "description": "Whether the filter offers an \"All\" option. false removes it, and the filter then always holds one value: the first, unless `start` names another."
              },
              "multiple": {
                "type": "boolean",
                "default": false,
                "description": "Render a checkbox multiselect instead of a dropdown; the rows shown are those matching any selected value."
              }
            }
          }
        ]
      }
    }
  }
}
